import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { inversoresApi, apiError, logout, mediaUrl, getUser } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Diamond, LogOut, FileText, Video, CalendarClock, BadgeDollarSign, Newspaper, LineChart, Target, User, Link2, Download, ExternalLink, Camera, KeyRound } from "lucide-react";
import { NotificationBell } from "../components/NotificationBell";

const IMG = {
  contrato: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/a1cf91ea2602d2cd39df9ed825cdc89983da979896579b934ed0a59ddd21a2c8.jpeg",
  connect: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/2930ae8708dcaeba51329eb921cacbb82bf57edea81e3dc4d27eac7ecc7c37c1.jpeg",
  meses: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/0b9b3c1aaae25a35427a0fb69dbc77f50973fb6ae927b6c173349f3721b65cc1.jpeg",
  pagos: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/15ef02a20d56ca553567644e5005357bf84374db9fc62b39bf888bf12cb48c47.jpeg",
  noticias: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/40f0ce7690fecb59f213ba085fc127ffbaa06dceb23dafec7c6541c42367ff6c.jpeg",
  reporte: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/0e9038362abad5104181be7c12933059b211892ac571ceaacdc7f2ac3bf76a29.jpeg",
  objetivos: "https://static.prod-images.emergentagent.com/jobs/c09f4645-686d-4e1e-a0f6-b7743ec7133b/images/7adcaea006c6eef5fd1fbab5b196729115a40223a5b3df2b7f6b1180bb38831f.jpeg",
};

const TILES = [
  { key: "contrato", label: "Contrato Legal", icon: FileText, img: IMG.contrato },
  { key: "connect", label: "Supera Connect", icon: Video, img: IMG.connect },
  { key: "meses", label: "Meses de pago", icon: CalendarClock, img: IMG.meses },
  { key: "pagos", label: "Pagos realizados", icon: BadgeDollarSign, img: IMG.pagos },
  { key: "noticias", label: "Noticias", icon: Newspaper, img: IMG.noticias },
  { key: "reporte", label: "Reporte Financiero", icon: LineChart, img: IMG.reporte },
  { key: "objetivos", label: "Objetivos Trimestrales", icon: Target, img: IMG.objetivos },
];

export default function InversoresPortal() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => inversoresApi.me().then(setD).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const signOut = () => { logout(); navigate("/login"); };
  const p = d?.perfil;

  return (
    <div className="min-h-screen bg-[#0A1C30] text-white" data-testid="inversores-portal">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#0A1C30]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><Diamond className="w-5 h-5 text-[var(--gold)] fill-[var(--gold)]" /><span className="font-extrabold tracking-[0.3em]">SUPERA</span><span className="text-white/40 text-sm hidden sm:inline">· Inversores</span></div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button onClick={() => setOpen("perfil")} data-testid="inv-portal-perfil" className="flex items-center gap-2 group">
              {p?.avatar_url ? <img src={mediaUrl(p.avatar_url)} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[var(--gold)]" /> : <div className="w-9 h-9 rounded-full bg-white/10 grid place-items-center font-bold">{(p?.nombre || "?")[0]}</div>}
              <span className="text-sm font-bold hidden sm:inline">{p?.nombre}</span>
            </button>
            <button onClick={signOut} data-testid="inv-logout" className="text-white/60 hover:text-white"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <p className="text-[var(--gold)] font-bold tracking-widest text-xs uppercase mb-2">Portal privado del inversionista</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Bienvenido, {p?.nombre || ""}.</h1>
        <p className="text-white/60 mt-3 max-w-2xl">Aquí encuentras toda tu información como inversionista Supera: contratos, reuniones, calendario de dividendos, pagos, reportes y la visión estratégica del programa.</p>
        {p?.status_inversor && <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/5 ring-1 ring-white/10 px-4 py-1.5 text-sm font-bold">{p.status_inversor}{p.status_inversor === "Franquicia" && p.franquicia_pais ? ` · ${p.franquicia_pais}` : ""}</div>}
      </section>

      {/* Tiles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {TILES.map((t, i) => {
            const Icon = t.icon;
            const count = t.key === "meses" ? (d?.meses?.length || 0) : t.key === "pagos" ? (d?.pagos?.length || 0) : t.key === "connect" ? (d?.sesiones?.length || 0) : (d?.contenido?.[t.key]?.length || 0);
            return (
              <button key={t.key} data-testid={`inv-tile-${t.key}`} onClick={() => setOpen(t.key)}
                className="group relative overflow-hidden rounded-2xl ring-1 ring-white/10 text-left aspect-[4/3] transition-transform hover:-translate-y-1"
                style={{ animation: `fadeUp .5s ease ${i * 0.06}s both` }}>
                <img src={t.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C30] via-[#0A1C30]/50 to-transparent" />
                <div className="absolute inset-0 p-4 sm:p-5 flex flex-col justify-end">
                  <div className="w-10 h-10 rounded-xl bg-[var(--gold)] grid place-items-center mb-2"><Icon className="w-5 h-5 text-[#0A1C30]" /></div>
                  <p className="font-extrabold text-base sm:text-lg leading-tight">{t.label}</p>
                  {count > 0 && <p className="text-white/60 text-xs mt-0.5">{count} {count === 1 ? "elemento" : "elementos"}</p>}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Section modal */}
      {open && open !== "perfil" && <SectionModal sectionKey={open} data={d} onClose={() => setOpen(null)} navigate={navigate} />}
      {open === "perfil" && <PerfilModal perfil={p} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

async function openBlob(promise) {
  try {
    const blob = await promise;
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiError(e, "No se pudo abrir el archivo.")); }
}

function SectionModal({ sectionKey, data, onClose, navigate }) {
  const LABELS = { contrato: "Contrato Legal", connect: "Supera Connect", meses: "Meses de pago", pagos: "Pagos realizados", noticias: "Noticias", reporte: "Reporte Financiero", objetivos: "Objetivos Trimestrales" };
  const isContent = ["contrato", "noticias", "reporte", "objetivos"].includes(sectionKey);
  const items = isContent ? (data?.contenido?.[sectionKey] || []) : [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`inv-modal-${sectionKey}`}>
        <DialogHeader><DialogTitle>{LABELS[sectionKey]}</DialogTitle><DialogDescription>Información exclusiva para ti como inversionista Supera.</DialogDescription></DialogHeader>

        {isContent && (
          <div className="space-y-3">
            {items.length === 0 && <p className="text-sm text-[#6B7688] py-6 text-center">Aún no hay contenido en esta sección. Muy pronto.</p>}
            {items.map((it) => (
              <div key={it.id} className="rounded-xl bg-[#F4F6F9] p-4">
                {it.titulo && <p className="font-extrabold text-[#0A1C30]">{it.titulo}</p>}
                {it.texto && <p className="text-sm text-[#3B4658] whitespace-pre-wrap mt-1">{it.texto}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-navy text-white font-bold px-3 py-1.5 text-xs"><ExternalLink className="w-3.5 h-3.5" /> Abrir enlace</a>}
                  {it.tiene_archivo && <button onClick={() => openBlob(inversoresApi.fileBlob(it.id))} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--gold)] text-[#0A1C30] font-bold px-3 py-1.5 text-xs"><Download className="w-3.5 h-3.5" /> {it.archivo_name || "Ver archivo"}</button>}
                </div>
              </div>
            ))}
          </div>
        )}

        {sectionKey === "meses" && (
          <div className="space-y-2">
            {(data?.meses || []).length === 0 && <p className="text-sm text-[#6B7688] py-6 text-center">Aún no hay meses de pago programados.</p>}
            {(data?.meses || []).map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-xl bg-[#F4F6F9] p-4">
                <div><p className="font-bold text-[#0A1C30]">{m.mes}</p>{m.nota && <p className="text-xs text-[#6B7688]">{m.nota}</p>}</div>
                <div className="text-right"><p className="font-extrabold text-[#0A1C30]">{m.moneda} {Number(m.monto).toLocaleString()}</p><span className={`text-xs font-bold ${m.estado === "pagado" ? "text-[#177A45]" : "text-[#B7791F]"}`}>{m.estado === "pagado" ? "Pagado" : "Programado"}</span></div>
              </div>
            ))}
          </div>
        )}

        {sectionKey === "pagos" && (
          <div className="space-y-2">
            {(data?.pagos || []).length === 0 && <p className="text-sm text-[#6B7688] py-6 text-center">Aún no hay pagos registrados.</p>}
            {(data?.pagos || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl bg-[#F4F6F9] p-4">
                <div><p className="font-bold text-[#0A1C30]">{p.fecha}</p><p className="text-xs text-[#6B7688]">{p.referencia}{p.nota ? ` · ${p.nota}` : ""}</p>{p.tiene_comprobante && <button onClick={() => openBlob(inversoresApi.receiptBlob(p.id))} className="inline-flex items-center gap-1 text-xs font-bold text-navy underline mt-1"><Download className="w-3 h-3" /> Comprobante</button>}</div>
                <p className="font-extrabold text-[#177A45]">{p.moneda} {Number(p.monto).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {sectionKey === "connect" && (
          <div className="space-y-2">
            <p className="text-sm text-[#6B7688]">Tus videollamadas privadas con Supera. Entra a la hora acordada.</p>
            {(data?.sesiones || []).length === 0 && <p className="text-sm text-[#6B7688] py-6 text-center">No tienes reuniones agendadas.</p>}
            {(data?.sesiones || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl bg-[#F4F6F9] p-4">
                <div><p className="font-bold text-[#0A1C30]">{s.titulo}</p><p className="text-xs text-[#6B7688]">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : "Sin fecha"} · {s.estado === "en_vivo" ? "EN VIVO" : s.estado}</p></div>
                <button data-testid={`inv-enter-${s.id}`} onClick={() => navigate(`/connect/room/${s.id}`)} className="rounded-lg bg-[var(--gold)] text-[#0A1C30] font-bold px-4 py-2 text-sm">Entrar</button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PerfilModal({ perfil, onClose, onSaved }) {
  const [f, setF] = useState({ nombre: perfil?.nombre || "", apellido_paterno: perfil?.apellido_paterno || "", apellido_materno: perfil?.apellido_materno || "", whatsapp: perfil?.whatsapp || "", ciudad: perfil?.ciudad || "", pais: perfil?.pais || "", edad: perfil?.edad || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const cp = perfil?.cuenta_pago || {};
  const save = async () => { try { await inversoresApi.updateMe(f); toast.success("Perfil actualizado."); onSaved(); } catch (e) { toast.error(apiError(e)); } };
  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Completa ambas contraseñas."); return; }
    try { await inversoresApi.changePassword(pw); toast.success("Contraseña actualizada."); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await inversoresApi.uploadAvatar(fd); toast.success("Foto actualizada."); onSaved(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="inv-perfil-modal">
        <DialogHeader><DialogTitle>Mi perfil</DialogTitle><DialogDescription>Actualiza tus datos, foto y contraseña.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-3 mb-2">
          {perfil?.avatar_url ? <img src={mediaUrl(perfil.avatar_url)} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-[var(--gold)]" /> : <div className="w-16 h-16 rounded-full bg-navy grid place-items-center text-white font-bold text-xl">{(perfil?.nombre || "?")[0]}</div>}
          <label className="flex items-center gap-1.5 text-sm font-bold text-navy cursor-pointer"><Camera className="w-4 h-4" /> Cambiar foto<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files[0])} /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-bold">Nombre</Label><Input value={f.nombre} onChange={(e) => set("nombre", e.target.value)} /></div>
          <div><Label className="text-xs font-bold">WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
          <div><Label className="text-xs font-bold">Apellido paterno</Label><Input value={f.apellido_paterno} onChange={(e) => set("apellido_paterno", e.target.value)} /></div>
          <div><Label className="text-xs font-bold">Apellido materno</Label><Input value={f.apellido_materno} onChange={(e) => set("apellido_materno", e.target.value)} /></div>
          <div><Label className="text-xs font-bold">Ciudad</Label><Input value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} /></div>
          <div><Label className="text-xs font-bold">País</Label><Input value={f.pais} onChange={(e) => set("pais", e.target.value)} /></div>
        </div>
        <button data-testid="inv-perfil-save" onClick={save} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5 mt-1">Guardar datos</button>

        <div className="rounded-xl bg-[#F4F6F9] p-4 mt-2">
          <p className="font-bold text-[#0A1C30] text-sm mb-1">Cuenta donde Supera realiza tus pagos</p>
          <p className="text-xs text-[#6B7688] mb-2">Por seguridad, solo Supera puede modificar estos datos.</p>
          <div className="text-sm text-[#3B4658] space-y-0.5">
            <p><b>Banco:</b> {cp.banco || "—"}</p>
            <p><b>Titular:</b> {cp.titular || "—"}</p>
            <p><b>CLABE:</b> {cp.clabe || "—"}</p>
            <p><b>Cuenta/Tarjeta:</b> {cp.cuenta || "—"}</p>
            {cp.moneda && <p><b>Moneda:</b> {cp.moneda}</p>}
            {cp.notas && <p><b>Notas:</b> {cp.notas}</p>}
          </div>
        </div>

        <div className="rounded-xl ring-1 ring-black/10 p-4 mt-2">
          <p className="font-bold text-[#0A1C30] text-sm mb-2 flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Cambiar contraseña</p>
          <div className="space-y-2">
            <Input type="password" placeholder="Contraseña actual" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} />
            <Input type="password" placeholder="Nueva contraseña" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} />
            <button data-testid="inv-pw-save" onClick={changePw} className="rounded-lg bg-navy text-white font-bold px-4 py-2 text-sm">Actualizar contraseña</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
